import { useState } from 'react';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { formatCurrency } from '@/lib/currency';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { SUBSCRIPTION_PROVIDERS, SUBSCRIPTION_STATUS, SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';
import { renewSubscription } from '@/lib/api/subscriptions';
import { getBillingCycle, getNextBillDateInformation } from '../lib/billing.logic';
import { useSubscriptionDetails } from '../queries/useSubscriptionDetails';
import { useCustomerId } from '../queries/useCustomerId';

/**
 * Hook to manage billing tab state and actions
 */
export const useBillingTab = () => {
    const { user, refetchUser } = useUser();
    const [loading, setLoading] = useState(false);
    const [isCancellationFlowOpen, setIsCancellationFlowOpen] = useState(false);
    const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
    const { data: subscriptionDetails } = useSubscriptionDetails();
    const { data: customerId } = useCustomerId();

    // Subscription info
    const platformSubscription = user?.subscriptions?.platform;
    const platformSubscriptionId = platformSubscription?.stripe_id || null;

    // Subscription type checks
    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;
    const isMobileSubscription =
        platformSubscription?.data?.provider?.toLowerCase() === SUBSCRIPTION_PROVIDERS.REVENUE_CAT;

    // Trial information
    const isNoExtraCostTrial = platformSubscription?.data?.trial_subscription_tier === SUBSCRIPTION_TIERS.CREATOR_PRO;
    const trialEndDate = platformSubscription?.valid_until;
    const trialExpirationMessage =
        subscriptionDetails?.status === 'trialing' && subscriptionDetails?.trial_end
            ? `Your trial expires on ${new Date(subscriptionDetails.trial_end * 1000).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
              })}`
            : null;

    // Discount/coupon information
    const activeCouponName = subscriptionDetails?.discount?.discount_code_name;
    const isFreeForever = subscriptionDetails?.discount?.is_free_forever || false;

    // Offer qualification logic (matching Vue)
    const isMonthlySubscription = platformSubscription?.data?.price?.interval === 'month';
    const isActiveSubscription = platformSubscription?.status === SUBSCRIPTION_STATUS.ACTIVE;
    const qualifiesForDiscount = isMonthlySubscription && isActiveSubscription;
    const hasThreeDayTrialExperiment = user?.experiments?.three_day_trial || false;
    // Check if user has already redeemed a cancellation offer
    const hasRedeemedOffer = !!(
        user?.tags?.platform_cancellation_free_month_applied ||
        user?.tags?.platform_cancellation_two_month_discount_applied
    );
    const shouldShowOffer = qualifiesForDiscount && !hasThreeDayTrialExperiment && !hasRedeemedOffer;
    // Check if user has taken the free month offer specifically
    const hasTakenFreeMonth = !!user?.tags?.platform_cancellation_free_month_applied;

    // Subscription display values
    const currentSubscriptionStatus = platformSubscription?.status ?? -1;
    const currentBilledPlan =
        platformSubscription?.data?.subscription_tier === SUBSCRIPTION_TIERS.CREATOR_PRO ? 'Creator Pro' : 'Creator';
    const billingCycle = getBillingCycle(platformSubscription);
    // Only show next bill date if NOT trialing (matches Vue behavior)
    // When trialing, only the trial expiration message should be shown
    const nextBillDate =
        subscriptionDetails?.status !== 'trialing'
            ? getNextBillDateInformation(subscriptionDetails?.next_billing_date)
            : null;
    const pricingDetail = platformSubscription?.data?.price
        ? `${formatCurrency(platformSubscription.data.price.amount, platformSubscription.data.price.currency?.toUpperCase() || 'USD')}/${platformSubscription.data.price.interval}`
        : '';

    // Check if user is in a free trial (no_cc_trial experiment) - matches Vue logic
    // Users in free trial with no payment method should not see the renew button
    const hasNoCcTrialExperiment = !!user?.experiments?.no_cc_trial;
    const remainingTrialDays = platformSubscription?.remaining_trial_days;
    const hasPaymentMethod = !!platformSubscription?.has_payment_method;
    const isInFreeTrial =
        hasNoCcTrialExperiment && remainingTrialDays !== undefined && remainingTrialDays > 0 && !hasPaymentMethod;

    // Actions
    const handleCancelSubscription = () => {
        if (isMobileSubscription) {
            stanNotify({
                type: 'info',
                title: 'Mobile Subscription',
                text: 'You subscribed on the Stan Mobile App. You can manage your subscription in the app settings.',
            });
        } else {
            setIsCancellationFlowOpen(true);
        }
    };

    const handleCancellationFlowComplete = () => {
        setIsCancellationFlowOpen(false);
        refetchUser();
    };

    const handleChangePlan = () => {
        if (isMobileSubscription) {
            stanNotify({
                type: 'info',
                title: 'Mobile Subscription',
                text: 'You subscribed on the Stan Mobile App. You can manage your subscription in the app settings.',
            });
        } else {
            setIsChangePlanModalOpen(true);
        }
    };

    const handleRenewSubscription = async () => {
        if (!platformSubscriptionId) {
            stanNotify({
                type: 'error',
                title: 'Error',
                text: 'No subscription found.',
            });
            return;
        }

        setLoading(true);
        try {
            await renewSubscription({ stripe_id: platformSubscriptionId });

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Your subscription has been renewed.',
            });

            refetchUser();
        } catch (error) {
            handleApiError(error, 'Renewal Failed');
        } finally {
            setLoading(false);
        }
    };

    const closeChangePlanModal = () => setIsChangePlanModalOpen(false);

    return {
        // State
        loading,
        isCancellationFlowOpen,
        isChangePlanModalOpen,
        customerId,

        // Subscription info
        platformSubscription,
        platformSubscriptionId,
        currentSubscriptionStatus,
        currentBilledPlan,
        billingCycle,
        nextBillDate,
        pricingDetail,

        // Subscription type flags
        isAffiliateOnlySubscription,
        isMobileSubscription,

        // Trial info
        isNoExtraCostTrial,
        trialEndDate,
        trialExpirationMessage,

        // Discount info
        activeCouponName,
        isFreeForever,

        // Offer qualification
        shouldShowOffer,
        hasTakenFreeMonth,

        // Free trial state (for hiding renew button)
        isInFreeTrial,

        // Actions
        handleCancelSubscription,
        handleCancellationFlowComplete,
        handleChangePlan,
        handleRenewSubscription,
        closeChangePlanModal,
    };
};
